<template>
  <div class="step-1 box mt-4">
    <div class="d-flex justify-content-between">
      <div class="section-title">1. School Information</div>
      <div v-if="showToggle" class="mt-2">
        <som-button button-type="clear" color="primary" v-on:click.prevent="toggle">
          <som-icon name="icon-chevron-down" size="sm"></som-icon>
        </som-button>
      </div>
    </div>
    <som-collapse ref="collapse" :collapsed="collapsed">
      <div class="form-content max-width-30">
        <fieldset>
          <legend>Is this tip related to a school incident?</legend>
          <div class="d-flex d-flex-row">
            <div class="custom-control custom-radio">
              <input type="radio" id="school-incident-yes" name="school-incident" class="custom-control-input" :value="true" checked v-model="isSchoolRelated" />
              <label class="custom-control-label" for="school-incident-yes">Yes</label>
            </div>
            <div class="custom-control custom-radio ml-4">
              <input type="radio" id="school-incident-no" name="school-incident" class="custom-control-input" :value="false" v-model="isSchoolRelated" />
              <label class="custom-control-label" for="school-incident-no">No</label>
            </div>
          </div>
        </fieldset>
        <div ref="school" v-if="isSchoolRelated == 1" class="form-group mt-4">
          <label for="schoolSelect">Michigan School</label>
          <div v-if="schoolSelected" class="bordered-content d-flex flex-row align-items-center justify-content-between">
            <div class="selected-school-content d-flex justify-content-center flex-column">
              <div class="bold">{{schoolSelected.name}}</div>
              <div>{{schoolSelected.address}}</div>
              <div>{{schoolSelected.city}}, {{schoolSelected.state}}, {{schoolSelected.zipCode}}</div>
            </div>
            <som-button button-type="outline" color="primary" size="sm" aria-label="Change selected school" @click.prevent="clearSelectedSchool">                      
              <div class="d-flex flex-row align-items-center">
                Change
              </div>
            </som-button>
          </div>
          <div v-else-if="enterSchoolInfoManually" class="container bordered-content">
            <div class="row">
              <div class="form-group col-12 enter-school-information-manually d-flex flex-row justify-content-between">
                <div>Enter school information manually</div>
                <som-button button-type="clear" color="primary" size="sm" aria-label="Remove manually entered school info" @click.prevent="clearSelectedSchool">                      
                  <som-icon name="icon-cancel" color="primary" size="sm"></som-icon>
                </som-button>
              </div>

              <div class="form-group col-12">
                <label for="manual-school-name">School Name</label>
                <input ref="schoolManualInfo" type="text" class="form-control" :class="{'is-invalid': schoolErrors.schoolName}" id="manual-school-name" v-model="schoolName" />
                <small v-if="schoolErrors.schoolName" class="form-text text-danger">This is a required field.</small>
              </div>
              
              <div class="form-group col-12">
                <label for="manual-school-address">Address <span class="label-optional">Optional</span></label>
                <input type="text" class="form-control" id="manual-school-address" v-model="schoolAddress" />
              </div>

              <div class="form-group col-12 col-md-6">
                <label for="manual-school-city">City</label>
                <input type="text" class="form-control" :class="{'is-invalid': schoolErrors.schoolCity}" id="manual-school-city" v-model="schoolCity"/>
                <small v-if="schoolErrors.schoolCity" class="form-text text-danger">This is a required field.</small>
              </div>

              <div class="form-group col-12 col-md-6">
                <label for="manual-school-state">State</label>
                <select class="form-control" id="manual-school-state" v-model="schoolState">
                  <option v-for="state in states" :key="state.abbreviation" :value="state.abbreviation">{{state.name}}</option>
                </select>
              </div>
            </div>
          </div>
          <div v-else class="school-search">
            <typeahead
              ref="schoolSearchInput"
              id="schoolSelect"
              :searching="searching"
              :search="onSearchInput"
              :dropdown-values="schoolSearchResults"
              :on-select-dropdown-value="onSelectSchool"
              placeholder="Search by school name or city"
              aria-label="Search by school name or city"
              :is-invalid="schoolErrors.schoolSearch"
            />
            <small v-if="schoolErrors.schoolSearch" class="form-text text-danger">This is a required field.</small>
          </div>
        </div>
          
        <div class="mt-3 d-flex justify-content-center">
          <som-button color="primary" v-on:click.prevent="continueFromStep1">Continue</som-button>
        </div>
      </div>
    </som-collapse>
  </div>
</template>

<script>

import Typeahead from './Typeahead';

const STATES = [
  {
      "name": "Alabama",
      "abbreviation": "AL"
  },
  {
      "name": "Alaska",
      "abbreviation": "AK"
  },
  {
      "name": "Arizona",
      "abbreviation": "AZ"
  },
  {
      "name": "Arkansas",
      "abbreviation": "AR"
  },
  {
      "name": "California",
      "abbreviation": "CA"
  },
  {
      "name": "Colorado",
      "abbreviation": "CO"
  },
  {
      "name": "Connecticut",
      "abbreviation": "CT"
  },
  {
      "name": "Delaware",
      "abbreviation": "DE"
  },
  {
      "name": "Florida",
      "abbreviation": "FL"
  },
  {
      "name": "Georgia",
      "abbreviation": "GA"
  },
  {
      "name": "Hawaii",
      "abbreviation": "HI"
  },
  {
      "name": "Idaho",
      "abbreviation": "ID"
  },
  {
      "name": "Illinois",
      "abbreviation": "IL"
  },
  {
      "name": "Indiana",
      "abbreviation": "IN"
  },
  {
      "name": "Iowa",
      "abbreviation": "IA"
  },
  {
      "name": "Kansas",
      "abbreviation": "KS"
  },
  {
      "name": "Kentucky",
      "abbreviation": "KY"
  },
  {
      "name": "Louisiana",
      "abbreviation": "LA"
  },
  {
      "name": "Maine",
      "abbreviation": "ME"
  },
  {
      "name": "Maryland",
      "abbreviation": "MD"
  },
  {
      "name": "Massachusetts",
      "abbreviation": "MA"
  },
  {
      "name": "Michigan",
      "abbreviation": "MI"
  },
  {
      "name": "Minnesota",
      "abbreviation": "MN"
  },
  {
      "name": "Mississippi",
      "abbreviation": "MS"
  },
  {
      "name": "Missouri",
      "abbreviation": "MO"
  },
  {
      "name": "Montana",
      "abbreviation": "MT"
  },
  {
      "name": "Nebraska",
      "abbreviation": "NE"
  },
  {
      "name": "Nevada",
      "abbreviation": "NV"
  },
  {
      "name": "New Hampshire",
      "abbreviation": "NH"
  },
  {
      "name": "New Jersey",
      "abbreviation": "NJ"
  },
  {
      "name": "New Mexico",
      "abbreviation": "NM"
  },
  {
      "name": "New York",
      "abbreviation": "NY"
  },
  {
      "name": "North Carolina",
      "abbreviation": "NC"
  },
  {
      "name": "North Dakota",
      "abbreviation": "ND"
  },
  {
      "name": "Ohio",
      "abbreviation": "OH"
  },
  {
      "name": "Oklahoma",
      "abbreviation": "OK"
  },
  {
      "name": "Oregon",
      "abbreviation": "OR"
  },
  {
      "name": "Pennsylvania",
      "abbreviation": "PA"
  },
  {
      "name": "Rhode Island",
      "abbreviation": "RI"
  },
  {
      "name": "South Carolina",
      "abbreviation": "SC"
  },
  {
      "name": "South Dakota",
      "abbreviation": "SD"
  },
  {
      "name": "Tennessee",
      "abbreviation": "TN"
  },
  {
      "name": "Texas",
      "abbreviation": "TX"
  },
  {
      "name": "Utah",
      "abbreviation": "UT"
  },
  {
      "name": "Vermont",
      "abbreviation": "VT"
  },
  {
      "name": "Virginia",
      "abbreviation": "VA"
  },
  {
      "name": "Washington",
      "abbreviation": "WA"
  },
  {
      "name": "West Virginia",
      "abbreviation": "WV"
  },
  {
      "name": "Wisconsin",
      "abbreviation": "WI"
  },
  {
      "name": "Wyoming",
      "abbreviation": "WY"
  }
];

const MAX_RESULTS_NUMBER = 20;
const MAP_SERVICE_URL = 'https://gisagocss.state.mi.us/arcgis/rest/services/CSS/Schools_Districts/MapServer/6/query?f=json&outFields=*&returnGeometry=false'

function mapSchoolAttributes(attributes) {
  // For 'https://gisagocss.state.mi.us/arcgis/rest/services/CSS/Schools_Districts/MapServer/6/query?f=json&outFields=*&returnGeometry=false'
  // Mapping from data retrieved
  // OFFICIALNM --> name
  // STREET --> address
  // CITY --> city
  // STATE --> state
  // ZIP --> zipCode
  // OBJECTID --> id
  // LATITUDE --> latitude
  // LONGITUDE --> longitude
  return {
    id: attributes.OBJECTID,
    name: attributes.OFFICIALNM,
    address: attributes.STREET,
    city: attributes.CITY,
    state: attributes.STATE,
    zipCode: `${attributes.ZIP}`.substring(0, 5),
    latitude: attributes.LATITUDE,
    longitude: attributes.LONGITUDE,
  }
}

async function findSchoolsByName(queryString) {
  try {
    // Make sure to encode the WHERE clause here to prevent any default encoding issues
    const q = encodeURIComponent(queryString.trim().toUpperCase());
  
    // Make sure to encode the WHERE clause here to prevent any default encoding issues
    const mapServiceUrl = `${MAP_SERVICE_URL}&where=UPPER(OFFICIALNM)%20LIKE%20%27%25${q}%25%27%20OR%20UPPER(CITY)%20LIKE%20%27%25${q}%25%27&resultRecordCount=50&orderByFields=OFFICIALNM`;

    const response = await fetch(mapServiceUrl);
    if (!response.ok) {
      // An error occurred with the request
      return [];
    }

    const data = await response.json();
    if (data.error) {
      // An error was returned from the server
      return [];
    }

    // Sort names like this:
    // 1. Names that "start" with the query string
    // 2. Alphabitcal order for the rest
    const startsWithReg = new RegExp(`^${queryString}`, 'i');
    return data.features.map((feature) => mapSchoolAttributes(feature.attributes)).sort((a, b) => {
      const aStartsWithQueryString = startsWithReg.test(a.name);
      const bStartsWithQueryString = startsWithReg.test(b.name);

      if ((aStartsWithQueryString && bStartsWithQueryString) || (!aStartsWithQueryString && !bStartsWithQueryString)) {
        // Both start with query string, or both don't, order them alphabetically
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }

        return 1;
      }

      if (aStartsWithQueryString) {
        // Only 'a' starts with the query string
        return -1;
      }

      // Only 'b' starts with the query string
      return 1;
    }).slice(0, MAX_RESULTS_NUMBER);
  } catch (e) {
    // Make sure to catch any errors
  }

  return [];
}


export default {
  name: 'SchoolInformation',
  components: {
    Typeahead
  },
  props: {
    collapsed: Boolean,
    showToggle: Boolean,
    toggle: Function,
    continue: Function,
  },
  data() {
    return {
      schoolSelect: '',
      states: STATES,

      isSchoolRelated: true,

      // School search
      searching: false,
      schoolSearchResults: [],
      schoolSelected: null,

      // Manually entered school
      enterSchoolInfoManually: false,
      schoolName: '',
      schoolAddress: '',
      schoolCity: '',
      schoolState: 'MI',
      schoolErrors: {},
    }
  },
  methods: {
    show() {
      this.$refs.collapse.show();
    },
    hide() {
      this.$refs.collapse.hide();
    },
    continueFromStep1() {
      let hasError = false;
      this.schoolErrors = {};

      if (this.isSchoolRelated) {
        if (this.enterSchoolInfoManually) {
          // School name and city are required
          if (!this.schoolName) {
            hasError = true;
            this.schoolErrors.schoolName = true;
          }

          if (!this.schoolCity) {
            hasError = true;
            this.schoolErrors.schoolCity = true;
          }
        } else if (!this.schoolSelected) {
          // No school was selected
          hasError = true;
          this.schoolErrors.schoolSearch = true;
        }
      }

      if (hasError) {
        this.$refs.school.scrollIntoView();
      } else {
        let schoolInfo = {
          isSchoolRelated: this.isSchoolRelated,
        };
        if (this.schoolSelected) {
          schoolInfo = {
            ...schoolInfo,
            ...this.schoolSelected
          };
        } else if (this.enterSchoolInfoManually) {
          schoolInfo = {
            ...schoolInfo,
            name: this.schoolName,
            address: this.schoolAddress,
            city: this.schoolCity,
            state: this.schoolState
          };
        }
        this.continue(schoolInfo);
      }
    },

    async searchSchools(q) {
      const schools = await findSchoolsByName(q);

      this.schoolSearchResults = [...schools, {
        id: null,
        name: 'Enter school information manually'
      }]
    },

    onSearchInput(queryString) {
      // Save query string in case we switch to manual entry for school
      this.schoolName = queryString;

      clearTimeout(this.searchBuffer);
      if (!queryString) {
      return;
      }

      this.searchBuffer = setTimeout(async () => {
        clearTimeout(this.searchBuffer);
        try {
          this.searching = true;
          await this.searchSchools(queryString);
        } catch (e) {
          // We just catch any errors here
        }
        this.searching = false;
      }, 300)
    },
    
    onSelectSchool(school) {
      if (school.id === null) {
        // Enter school information manually
        this.updateFocusElement = 'schoolManualInfo';
        this.enterSchoolInfoManually = true;
      } else {
        this.enterSchoolInfoManually = false;
        this.schoolSelected = school;
      }
    },
  
    clearSelectedSchool() {
      this.updateFocusElement = 'schoolSearchInput';
      this.schoolSelected = null;
      this.enterSchoolInfoManually = false;
      this.schoolName = '';
    },
  },

  updated() {
    if (this.updateFocusElement) {
      this.$refs[this.updateFocusElement] && this.$refs[this.updateFocusElement].focus();
      this.updateFocusElement = null;
    }
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .selected-school-box {
    display: flex;
    flex-direction: 'row';
  }

  .selected-school-content {
    flex: 1;
  }

  .header-box {
    margin: 0 auto;
  }

  .logo-image {
    width: 100%;
    max-width: 16rem;
    height: auto;
  }

  .subtitle {
    font-size: 1.2rem;
  }
  @media screen and (max-width: 600px) {
    .subtitle {
      font-size: 1rem;
    }
  }

  .bold {
    font-weight: bold;
  }

  .box {
    background-color: white;
  }

  .max-width-30 {
    max-width: 30rem;
  }

  .max-width-40 {
    max-width: 40rem;
  }

  .form-content {
    padding: 2rem;
    padding-top: 0;
    margin: 0 auto;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .section-title {
    font-family: Montserrat,sans-serif;
    font-weight: 700;
    line-height: 1.1;
    font-size: 1.375rem;
    letter-spacing: .1px;
    padding: 1rem;
    margin: 0;
    color: rgb(52, 118, 163);
    white-space: nowrap; 
    overflow: hidden;
    text-overflow: ellipsis;
  }
  @media screen and (max-width: 600px) {
    .section-title {
      font-size: 1.2rem;
    }
  }

  .special-label {
    text-align: center;
    padding: 1rem;
    margin-bottom: 1rem;
    border-bottom: 1px solid rgb(236, 236, 236);
  }

  textarea {
    height: 8rem;
  }

  textarea,
  select,
  input {
    font-size: 0.875rem;
  }

  textarea::placeholder {
    color: #aaa;

  }

  .message {
    margin: 0 auto;
  }

  .align-center {
    margin: 0 auto;
  }

  .border-top {
    border-top: 1px solid rgb(29, 25, 25);
  }

  .border-top-light {
    border-top: 1px solid rgb(236, 236, 236);
  }

  .text-bold {
    font-weight: bold;
  }

  .enter-school-information-manually {
    margin-bottom: 1rem;
  }

  .label-optional {
    font-size: 0.6rem;
    color: rgb(110, 110, 110);
    margin-left: 0.25rem;
  }

  .radio-container {
    padding: 0.5rem 1rem;
  }

  .radio-container.is-invalid {
    border-radius: .25rem;
    border: 1px solid #b80000;
  }

  .bordered-content {
    border: 1px solid rgba(0,0,0,.125);
    border-radius: 0.25rem;
    padding: .75rem 1.25rem;
  }

  .school-search {
    position: relative;
  }
</style>

<style>
</style>